import { deepClone } from "@/util/func";
import { spaceValidator } from "@/util/func";
import { getModule } from "vuex-module-decorators";
import { Input, Modal, Form, Select, Drawer, Button } from "ant-design-vue";
import { Vue, Component, Watch } from "vue-property-decorator";
import { ApplicationViews } from "@/util/enum";
import { CommonEntitiesModule } from "@/modules/common-entities";
import AppSettings from "@/modules/app-settings";
import { fmtMsg } from "commonui";
import { CommonLocale, CommonEntitiesLocale } from "@/locales/localeid";
import DurationSelect from "@/components/content-exposure/duration-select/duration-select.vue";
import { VersionGroupModel } from "@/services/version-group";
import RichTextSingleLineEditor from "@/components/rich-text-single-line-editor/rich-text-single-line-editor.vue";
import { uniq } from "lodash";
import { pureRichText } from "@/util/func";
import { FieldDecoratorOptions } from "ant-design-vue/lib";

@Component({
	components: {
		Form,
		Modal,
		Input,
		Select,
		Drawer,
		Button,
		DurationSelect,
		Item: Form.Item,
		Option: Select.Option,
		RichTextSingleLineEditor,
		TextArea: Input.TextArea
	}
})
export default class CommonEntityModal extends Vue {
	public ApplicationViews = ApplicationViews;
	private appSettings = getModule(AppSettings);
	public CommonLocale = CommonLocale;
	public CommonEntitiesModule = CommonEntitiesModule;
	public CommonEntitiesLocale = CommonEntitiesLocale;
	public fmtMsg = fmtMsg;
	public commonEntityForm;
	public defaultVersionGroup = "";
	public inputEntityText = "";
	public validEntityText: any = null;
	public maxTextLength = 500;
	public teachingActivityMaxLength = 4000;
	public durationValue = "1:00";

	created() {
		this.commonEntityForm = this.$form.createForm(this, { name: "commonEntityForm" });
	}

	get isVisible() {
		return CommonEntitiesModule.isVisible;
	}

	get isEdit() {
		return CommonEntitiesModule.isEditEntity;
	}

	get cancelText() {
		return fmtMsg(CommonLocale.CommonCancelButtonText);
	}

	get saveText() {
		return fmtMsg(CommonLocale.CommonSaveButtonText);
	}

	get isTeachingActivtities() {
		return this.appSettings.childMenuSelected == ApplicationViews.TeachingActivities;
	}

	get entities() {
		return this.isTeachingActivtities ? CommonEntitiesModule.teachingActivities : CommonEntitiesModule.transitions;
	}

	get modalTitle() {
		return this.isEdit ? this.editEntityTitle : this.NewEntityTitle;
	}

	get inputTitle() {
		return this.isTeachingActivtities
			? fmtMsg(CommonEntitiesLocale.CommonActivitiesText)
			: fmtMsg(CommonEntitiesLocale.CommonModalTransition);
	}

	get NewEntityTitle() {
		return this.isTeachingActivtities
			? fmtMsg(CommonEntitiesLocale.CommonAddActivityText)
			: fmtMsg(CommonEntitiesLocale.CommonModalAddTransition);
	}

	get editEntityTitle() {
		return this.isTeachingActivtities
			? fmtMsg(CommonEntitiesLocale.CommonEditActivity)
			: fmtMsg(CommonEntitiesLocale.CommonEditTansition);
	}

	get customTextIsRequired() {
		return fmtMsg(CommonLocale.CommonFieldRequired, { name: this.inputTitle });
	}

	get customTextFieldLength() {
		return fmtMsg(CommonLocale.CommonFieldLength, {
			name: this.inputTitle,
			value: this.isTeachingActivtities ? this.teachingActivityMaxLength : this.maxTextLength
		});
	}

	get customTextFieldExisted() {
		return this.isTeachingActivtities
			? fmtMsg(CommonEntitiesLocale.CommonTeachingActivityTextHasExisted)
			: fmtMsg(CommonEntitiesLocale.CommonTransitionTextHasExisted);
	}

	get versionGroups(): VersionGroupModel[] {
		return CommonEntitiesModule.versionGroups;
	}

	get entityData() {
		return CommonEntitiesModule.selectedEntity;
	}

	get commonDecorator(): [string, FieldDecoratorOptions] {
		const length = this.isTeachingActivtities ? this.teachingActivityMaxLength : this.maxTextLength;
		return [
			"text",
			{
				initialValue: this.inputEntityText,
				rules: [
					{
						required: true,
						message: fmtMsg(CommonLocale.CommonFieldRequired, { name: this.inputTitle }),
						trigger: "change"
					},
					{
						max: length,
						message: fmtMsg(CommonLocale.CommonFieldLength, { name: this.inputTitle, value: length }),
						trigger: "change"
					},
					{
						validator: (rule, value, callback) => {
							return spaceValidator(
								this.commonEntityForm,
								"text",
								fmtMsg(CommonLocale.CommonFieldRequired, { name: this.inputTitle }),
								rule,
								value,
								callback
							);
						}
					},
					{
						validator: (rule, value, callback) => {
							let entities = deepClone(this.entities);
							if (this.isEdit) {
								entities = entities.filter((v) => v.id != this.entityData.id);
							}
							const commonText = (entities as any)
								.map((d) => d.text.trim())
								.concat([value.trim()]) as string[];
							if (commonText.length != uniq(commonText).length) {
								callback(this.customTextFieldExisted);
							} else {
								callback();
							}
						}
					}
				]
			}
		];
	}

	onVersionGroupChanged(value: string): void {
		this.defaultVersionGroup = value;
		CommonEntitiesModule.setVersionGroupId(value);
	}

	@Watch("isVisible")
	setInputEntityText() {
		if (this.isVisible) {
			this.inputEntityText = this.isEdit
				? this.entityData.text
				: this.isTeachingActivtities
				? ""
				: `<b>${fmtMsg(CommonEntitiesLocale.CommonModalTransitionText)}</b> `;
			this.durationValue = this.isEdit
				? this.entityData.duration && this.toTimeString(this.entityData.duration)
				: "1:00";
		} else {
			this.validEntityText = null;
			CommonEntitiesModule.setSelectedEntity({});
		}
	}

	@Watch("isEdit", { immediate: true })
	@Watch("entityData", { immediate: true })
	@Watch("versionGroups", { immediate: true })
	initEditVersionGroup() {
		const { versionGroupId } = this.entityData;
		const initVersionGroupId = this.isEdit ? versionGroupId : this.versionGroups[0].id;
		this.defaultVersionGroup = initVersionGroupId;
		CommonEntitiesModule.setVersionGroupId(initVersionGroupId);
	}

	@Watch("isVisible")
	clearForm() {
		!this.isVisible && this.commonEntityForm.resetFields();
	}

	toTimeString(second) {
		const sec = second % 60;
		return `${Math.floor(second / 60)}:${sec > 9 ? sec : `0${sec}`}`;
	}

	toSeconds(time: string) {
		const times = time.split(":");
		return parseInt(times[0]) * 60 + parseInt(times[1]);
	}

	onStartChange(value) {
		this.durationValue = value;
	}

	editEntityApi(values) {
		const { id } = this.entityData;
		if (this.isTeachingActivtities) {
			CommonEntitiesModule.updateTeachingActivity({ id, ...values });
		} else {
			CommonEntitiesModule.updateTransition({ id, ...values, duration: this.toSeconds(this.durationValue) });
		}
	}

	newEntityApi(values) {
		if (this.isTeachingActivtities) {
			CommonEntitiesModule.addTeachingActivity({ ...values });
		} else {
			CommonEntitiesModule.addTransition({ ...values, duration: this.toSeconds(this.durationValue) });
		}
	}

	validateTitle(rule, value, callback) {
		const formatValue = pureRichText(value);
		if (formatValue && !formatValue.trim()) {
			callback(fmtMsg(CommonLocale.CommonFieldRequired, { name: this.inputTitle }));
			return;
		}
		callback();
	}

	handleCancel() {
		this.commonEntityForm.resetFields();
		CommonEntitiesModule.setVisibleModal(false);
	}

	handleSubmit() {
		this.commonEntityForm.validateFields((err, values) => {
			if (!err) {
				CommonEntitiesModule.setVisibleModal(false);
				const entity = { text: values.text.trim(), versionGroupId: this.defaultVersionGroup };
				this.isEdit ? this.editEntityApi(entity) : this.newEntityApi(entity);
			} else {
				// this.setLoading(false);
			}
		});
	}
}
